/**
 * The known OpenAI models an LLM Profile can run under, for the model picker
 * (spec 160, mirroring spec 141's backend-side curated list). Hand-maintained —
 * not fetched from any service — and kept in lockstep with the backend's own
 * `_OPENAI_MODELS` set (`product-x-next-service/src/app/features/llm_profiles/validation.py`),
 * which is the actual source of truth enforced at submit time.
 */

export interface OpenAiModelEntry {
  /** The exact model identifier this entry sets the field to. */
  id: string;
  /** The human-readable label shown above `id` in the item. */
  displayName: string;
  /** Still callable, but no longer recommended for new profiles. */
  deprecated?: boolean;
}

export interface OpenAiModelTier {
  tier: string;
  /** Shown once beside the tier heading, not repeated per item. */
  efficiency: string;
  models: readonly OpenAiModelEntry[];
}

export const OPENAI_MODEL_CATALOG: readonly OpenAiModelTier[] = [
  {
    tier: 'Legacy',
    efficiency: 'Previous generation',
    models: [
      { id: 'gpt-3.5-turbo', displayName: 'GPT-3.5 Turbo' },
      { id: 'gpt-4o', displayName: 'GPT-4o' },
    ],
  },
  {
    tier: 'GPT-5',
    efficiency: 'General purpose',
    models: [
      { id: 'gpt-5', displayName: 'GPT-5' },
      { id: 'gpt-5-mini', displayName: 'GPT-5 mini' },
      { id: 'gpt-5-nano', displayName: 'GPT-5 nano' },
      { id: 'gpt-5.1', displayName: 'GPT-5.1' },
      { id: 'gpt-5.2', displayName: 'GPT-5.2' },
      { id: 'gpt-5.4', displayName: 'GPT-5.4' },
      { id: 'gpt-5.5', displayName: 'GPT-5.5' },
      { id: 'gpt-5.6-luna', displayName: 'GPT-5.6 Luna' },
      { id: 'gpt-5.6-sol', displayName: 'GPT-5.6 Sol' },
      { id: 'gpt-5.6-terra', displayName: 'GPT-5.6 Terra' },
    ],
  },
  {
    tier: 'GPT-6',
    efficiency: 'Most capable',
    models: [
      { id: 'gpt-6-astra', displayName: 'GPT-6 Astra' },
      { id: 'gpt-6-luna', displayName: 'GPT-6 Luna' },
      { id: 'gpt-6-sol', displayName: 'GPT-6 Sol' },
      { id: 'gpt-6.1-sol', displayName: 'GPT-6.1 Sol' },
    ],
  },
  {
    tier: 'o-series',
    efficiency: 'Reasoning',
    models: [
      { id: 'o1', displayName: 'o1' },
      { id: 'o3', displayName: 'o3' },
      { id: 'o3-mini', displayName: 'o3-mini' },
      { id: 'o4-mini', displayName: 'o4-mini' },
    ],
  },
];
